export const bundleQuery = `query FaunivaBundleComponents($id: ID!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
  node(id: $id) {
    ... on ProductVariant {
      id
      requiresComponents
      price { amount currencyCode }
      components(first: 30) {
        nodes {
          quantity
          productVariant { id title price { amount currencyCode } product { title handle } }
        }
        pageInfo { hasNextPage }
      }
    }
  }
}`;

export function moneyMinor(amount) {
  if (typeof amount !== 'string' || !/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(amount)) return null;
  const [whole, decimal = ''] = amount.split('.');
  const result = Number(whole) * 100 + Number(decimal.padEnd(2, '0'));
  return Number.isSafeInteger(result) ? result : null;
}

export function bundleValue(variant, currency) {
  if (!variant || typeof variant.requiresComponents !== 'boolean') throw new Error('Invalid bundle response');
  if (!variant.requiresComponents) return null;
  const total = moneyMinor(variant.price?.amount);
  const connection = variant.components;
  if (total === null || variant.price.currencyCode !== currency || !connection || connection.pageInfo?.hasNextPage !== false || !Array.isArray(connection.nodes) || !connection.nodes.length) throw new Error('Incomplete bundle');
  let separate = 0;
  const items = connection.nodes.map(component => {
    const item = component?.productVariant;
    const price = moneyMinor(item?.price?.amount);
    if (!Number.isSafeInteger(component?.quantity) || component.quantity < 1 || price === null || item.price.currencyCode !== currency || !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(item.id) || !item.product?.title || !item.product?.handle) throw new Error('Invalid component');
    separate += price * component.quantity;
    if (!Number.isSafeInteger(separate)) throw new Error('Invalid total');
    return {quantity: component.quantity, title: item.product.title, variantTitle: item.title, handle: item.product.handle, id: item.id.split('/').pop()};
  }).sort((a, b) => a.id.localeCompare(b.id));
  return {items, separate, savings: Math.max(0, separate - total)};
}

const requests = new Map();
async function loadBundle(id, country, language) {
  const key = `${id}:${country}:${language}`;
  const cached = requests.get(key);
  if (cached && Date.now() - cached.time < 30000) return cached.promise;
  const promise = (async () => {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 8000);
    try {
      const response = await fetch('/api/2026-07/graphql.json', {
        method: 'POST', credentials: 'same-origin', signal: abort.signal,
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({query: bundleQuery, variables: {id, country, language}})
      });
      if (!response.ok) throw new Error('Bundle request unavailable');
      const result = await response.json();
      if (result.errors?.length || result.data?.node?.id !== id) throw new Error('Bundle unavailable');
      return result.data.node;
    } finally { clearTimeout(timer); }
  })();
  requests.set(key, {promise, time: Date.now()});
  promise.catch(() => { if (requests.get(key)?.promise === promise) requests.delete(key); });
  return promise;
}

if (typeof document !== 'undefined') {
  class FaunivaBundleValue extends HTMLElement {
    connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.abort = new AbortController();
      document.addEventListener('variant:change', event => {
        if (!this.dataset.formId || event.detail?.formId !== this.dataset.formId) return;
        const id = event.detail.variant?.id;
        if (!id) { this.generation++; this.hidden = true; return; }
        this.dataset.variantId = `gid://shopify/ProductVariant/${id}`;
        this.refresh();
      }, {signal: this.abort.signal});
      if (!this.dataset.renderedVariant || !('IntersectionObserver' in window) || window.Shopify?.designMode) this.refresh();
      else {
        this.visibility = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.visibility.disconnect();
          const image = document.querySelector('.fauniva-hero__artwork, product-gallery img[fetchpriority="high"]');
          if (!image || image.complete) this.refresh();
          else {
            let started = false;
            const ready = () => {
              if (started || !this.isConnected) return;
              started = true;
              clearTimeout(this.initialTimer);
              this.refresh();
            };
            image.addEventListener('load', ready, {once: true, signal: this.abort.signal});
            image.addEventListener('error', ready, {once: true, signal: this.abort.signal});
            this.initialTimer = setTimeout(ready, 2500);
          }
        }, {rootMargin: '600px 0px'});
        this.visibility.observe(this.parentElement);
      }
    }
    disconnectedCallback() { this.generation++; this.abort?.abort(); this.visibility?.disconnect(); clearTimeout(this.initialTimer); }
    async refresh() {
      const generation = ++this.generation;
      const output = this.querySelector('[data-bundle-output]');
      const keepOutput = this.dataset.renderedVariant === this.dataset.variantId && Boolean(output?.textContent.trim());
      if (!keepOutput) this.hidden = true;
      if (!output || !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(this.dataset.variantId)) return;
      if (!keepOutput) output.replaceChildren();
      try {
        const variant = await loadBundle(this.dataset.variantId, this.dataset.country, this.dataset.language);
        if (generation !== this.generation || !this.isConnected) return;
        const value = bundleValue(variant, this.dataset.currency);
        if (!value) {
          const fallback = this.querySelector('template[data-bundle-fallback]');
          if (keepOutput) this.hidden = false;
          else if (fallback?.content.textContent.trim()) { output.replaceChildren(fallback.content.cloneNode(true)); this.hidden = false; }
          return;
        }
        const next = document.createDocumentFragment();
        const labels = JSON.parse(this.querySelector('[data-bundle-labels]').textContent);
        const heading = document.createElement('p');
        heading.className = 'bold'; heading.textContent = labels.contents;
        const list = document.createElement('ul'); list.setAttribute('role', 'list');
        const root = this.dataset.root || '/';
        value.items.forEach(item => {
          const li = document.createElement('li');
          if (this.dataset.context !== 'showroom' || item.quantity !== 1) li.append(document.createTextNode(`${item.quantity} × `));
          const link = document.createElement('a'); link.className = 'link';
          link.href = `${root}products/${encodeURIComponent(item.handle)}?variant=${item.id}`;
          link.textContent = item.title + (item.variantTitle && item.variantTitle !== 'Default Title' ? ` · ${item.variantTitle}` : '');
          li.append(link); list.append(li);
        });
        if (this.dataset.context === 'showroom') {
          const summary = document.createElement('p');
          summary.className = 'fauniva-bundles__summary';
          summary.textContent = value.items.map(item => (item.quantity > 1 ? `${item.quantity} × ` : '') + (labels.short_names?.[item.handle] || item.title)).join(' + ');
          const count = document.createElement('p');
          count.className = 'fauniva-bundles__piece-count';
          const pieceCount = value.items.reduce((sum, item) => sum + item.quantity, 0);
          const desktopCount = document.createElement('span');
          desktopCount.className = 'fauniva-showroom-desktop';
          desktopCount.textContent = labels.pieces.replace('__COUNT__', pieceCount);
          const mobileCount = document.createElement('span');
          mobileCount.className = 'fauniva-showroom-mobile';
          mobileCount.textContent = (labels.pieces_mobile || labels.pieces).replace('__COUNT__', pieceCount);
          count.append(desktopCount, mobileCount);
          next.append(count, summary);
        } else next.append(heading, list);
        if (this.dataset.showSavings === 'true' && value.savings > 0) {
          const money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style: 'currency', currency: this.dataset.currency});
          const rows = [[labels.separately, value.separate, 'text-subdued'], [labels.save, value.savings, 'bold']];
          if (this.dataset.context === 'showroom') rows.reverse();
          for (const [label, amount, className] of rows) {
            const text = document.createElement('p'); text.className = className;
            if (this.dataset.context === 'showroom') text.classList.add(amount === value.savings ? 'fauniva-bundles__saving' : 'fauniva-bundles__comparison');
            const formattedAmount = money.format(amount / 100);
            if (this.dataset.context === 'showroom' && amount === value.separate && labels.separately_mobile) {
              const desktopComparison = document.createElement('span');
              desktopComparison.className = 'fauniva-showroom-desktop';
              desktopComparison.textContent = label.replace(/__AMOUNT__/g, formattedAmount);
              const mobileComparison = document.createElement('span');
              mobileComparison.className = 'fauniva-showroom-mobile';
              mobileComparison.textContent = labels.separately_mobile.replace(/__AMOUNT__/g, formattedAmount);
              text.append(desktopComparison, mobileComparison);
            } else text.textContent = label.replace(/__AMOUNT__/g, formattedAmount);
            next.append(text);
          }
        }
        output.replaceChildren(next);
        this.dataset.renderedVariant = this.dataset.variantId;
        this.hidden = false;
      } catch { if (generation === this.generation) this.hidden = !keepOutput; }
    }
  }
  if (!customElements.get('fauniva-bundle-value')) customElements.define('fauniva-bundle-value', FaunivaBundleValue);
}

